import React, { useState, useEffect, useCallback } from 'react';
import { Settings, Loader2, AlertCircle, Check } from 'lucide-react';
import Layout from './Layout';
import { AU_STATES } from '../constants/auStates';
import { getFleetSettingsApi, updateFleetSettingsApi } from '../services/fleetApi';

/*
 * The fleet customer's own contact details.
 *
 * This is the only fleet portal screen that writes to the Customer record itself rather
 * than to something hanging off it, which is why the two read-only fields matter:
 *
 *  - EMAIL is the portal login identity. The server refuses to write it, and showing it
 *    as an editable box that silently does nothing is worse than showing it plainly.
 *  - NAME is what prints on their invoices and how the workshop files them.
 *
 * Both are rendered, neither is editable, and the note under them says who to ask.
 */

const Field = ({ label, value, onChange, placeholder, maxLength, autoComplete, disabled }) => (
    <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
        <input
            type="text"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            maxLength={maxLength}
            autoComplete={autoComplete}
            disabled={disabled}
            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
        />
    </div>
);

const ReadOnlyField = ({ label, value }) => (
    <div>
        <span className="block text-sm font-medium text-gray-700 mb-1.5">{label}</span>
        <p className="px-3 py-2.5 rounded-xl bg-gray-50 border border-gray-100 text-sm text-gray-600 break-words">
            {value || <span className="text-gray-400">—</span>}
        </p>
    </div>
);

const EMPTY = { phone: '', address: '', suburb: '', state: '', postcode: '' };

const FleetSettingsPage = () => {
    const [details, setDetails] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [saveError, setSaveError] = useState(null);
    const [saved, setSaved] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getFleetSettingsApi();
            setDetails(data);
            /*
             * Null from the server becomes '' in the form. That is deliberate and it is
             * what makes "clear this field" work: the form always sends every field, so
             * an emptied box arrives as '' and the server clears the column. If nulls
             * were kept as null, the server's null-means-leave-alone rule would silently
             * refuse every deletion.
             */
            setForm({
                phone: data.phone ?? '',
                address: data.address ?? '',
                suburb: data.suburb ?? '',
                state: data.state ?? '',
                postcode: data.postcode ?? '',
            });
        } catch (e) {
            setError(e?.response?.data?.message || 'Could not load your details.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const set = (field) => (value) => {
        setForm((p) => ({ ...p, [field]: value }));
        setSaved(false);
        setSaveError(null);
    };

    const handleSave = async () => {
        setSaving(true);
        setSaveError(null);
        setSaved(false);
        try {
            const updated = await updateFleetSettingsApi(form);
            setDetails(updated);
            setSaved(true);
        } catch (e) {
            setSaveError(e?.response?.data?.message || 'Could not save your details.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Layout>
            <div className="max-w-2xl">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center border border-emerald-100">
                        <Settings size={20} className="text-emerald-600" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-display font-semibold text-gray-900">Your details</h1>
                        <p className="text-sm text-gray-500">
                            The address your workshop uses for invoices and paperwork.
                        </p>
                    </div>
                </div>

                {loading ? (
                    <div className="bg-white rounded-2xl border border-gray-200 py-16 flex flex-col items-center">
                        <Loader2 size={28} className="text-emerald-500 animate-spin" />
                        <p className="mt-3 text-sm text-gray-500">Loading your details…</p>
                    </div>
                ) : error ? (
                    <div className="bg-white rounded-2xl border border-gray-200 py-16 flex flex-col items-center px-6 text-center">
                        <AlertCircle size={28} className="text-red-500" />
                        <p className="mt-3 text-sm text-gray-900 font-medium">{error}</p>
                        <button
                            onClick={load}
                            className="mt-4 px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm font-semibold hover:bg-emerald-700 border-none cursor-pointer"
                        >
                            Try again
                        </button>
                    </div>
                ) : (
                    <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-5">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <ReadOnlyField label="Business" value={details?.name} />
                            <ReadOnlyField label="Email" value={details?.email} />
                        </div>
                        <p className="text-xs text-gray-500 -mt-2">
                            Your business name and sign-in email are managed by your workshop —
                            ask them to change either.
                        </p>

                        <div className="border-t border-gray-100 pt-5 space-y-5">
                            <Field
                                label="Phone" value={form.phone} onChange={set('phone')}
                                placeholder="03 9000 0000" maxLength={20} autoComplete="tel"
                                disabled={saving}
                            />
                            <Field
                                label="Address" value={form.address} onChange={set('address')}
                                placeholder="12 Smith St" maxLength={500} autoComplete="street-address"
                                disabled={saving}
                            />
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                                <Field
                                    label="Suburb" value={form.suburb} onChange={set('suburb')}
                                    placeholder="Richmond" maxLength={100} autoComplete="address-level2"
                                    disabled={saving}
                                />
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">State</label>
                                    <select
                                        value={form.state}
                                        onChange={(e) => set('state')(e.target.value)}
                                        disabled={saving}
                                        className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:bg-gray-50"
                                    >
                                        {/* An empty option, because "not supplied" is a real answer
                                            and without it a blank state can never be re-cleared. */}
                                        <option value="">—</option>
                                        {AU_STATES.map((s) => (
                                            <option key={s} value={s}>{s}</option>
                                        ))}
                                    </select>
                                </div>
                                <Field
                                    label="Postcode" value={form.postcode} onChange={set('postcode')}
                                    placeholder="3121" maxLength={10} autoComplete="postal-code"
                                    disabled={saving}
                                />
                            </div>
                        </div>

                        {saveError && (
                            <div className="flex items-start gap-2 rounded-xl bg-red-50 border border-red-100 px-3 py-2.5">
                                <AlertCircle size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-red-700">{saveError}</p>
                            </div>
                        )}

                        <div className="flex items-center gap-3 pt-1">
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="inline-flex items-center gap-2 rounded-xl border-none bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 cursor-pointer disabled:opacity-60"
                            >
                                {saving && <Loader2 size={16} className="animate-spin" />}
                                {saving ? 'Saving…' : 'Save'}
                            </button>
                            {saved && !saving && (
                                <span className="inline-flex items-center gap-1.5 text-sm text-emerald-700">
                                    <Check size={16} /> Saved
                                </span>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default FleetSettingsPage;
